import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, doc, getDoc, getDocs, getDocFromServer, collection, onSnapshot, query, where, setDoc, addDoc, updateDoc, deleteDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Import the Firebase configuration
import firebaseConfig from '../firebase-applet-config.json';

export interface EnvironmentAuthInfo {
  environmentType: 'codespaces' | 'aistudio' | 'production' | 'localhost' | 'custom';
  hostname: string;
  host: string;
  authDomain: string;
  redirectUri: string;
  isCustomOverride: boolean;
  upstreamDefaultDomain: string;
  upstreamDefaultRedirectUri: string;
}

function cleanDomain(domain: string): string {
  return (domain || '').replace(/^https?:\/\//i, '').replace(/:\d+$/, '').replace(/\/+$/, '').trim();
}

/**
 * Detects the runtime environment context to provide the correct authDomain for Google OAuth:
 * - GitHub Codespaces (*.app.github.dev / *.githubpreview.dev)
 * - AI Studio Dev / Preview (*.run.app)
 * - Cloud Run Production (rfxpress.ai.studio / custom domain)
 * - Localhost dev (localhost:3000)
 */
export function detectEnvironmentAuth(): EnvironmentAuthInfo {
  const rawDefault = (firebaseConfig as any).authDomain || 'apexcite-3aece.firebaseapp.com';
  const defaultDomain = cleanDomain(rawDefault) || 'apexcite-3aece.firebaseapp.com';
  const defaultRedirectUri = `https://${defaultDomain}/__/auth/handler`;

  if (typeof window === 'undefined') {
    return {
      environmentType: 'custom',
      hostname: 'server',
      host: 'server',
      authDomain: defaultDomain,
      redirectUri: defaultRedirectUri,
      isCustomOverride: false,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  const hostname = window.location.hostname;
  const host = window.location.host;
  const protocol = window.location.protocol;

  // 1. Manual local storage override (allows developer toggling between context host and default)
  const rawStoredOverride = window.localStorage.getItem('rfx_custom_auth_domain');
  if (rawStoredOverride) {
    const storedOverride = cleanDomain(rawStoredOverride);
    const isDefault = storedOverride === defaultDomain;
    return {
      environmentType: hostname.endsWith('.app.github.dev') || hostname.endsWith('.githubpreview.dev')
        ? 'codespaces'
        : hostname.includes('.run.app')
        ? 'aistudio'
        : hostname === 'rfxpress.ai.studio' || hostname.includes('rfxpress')
        ? 'production'
        : hostname === 'localhost' || hostname === '127.0.0.1'
        ? 'localhost'
        : 'custom',
      hostname,
      host,
      authDomain: storedOverride,
      redirectUri: isDefault ? defaultRedirectUri : `${protocol}//${storedOverride}/__/auth/handler`,
      isCustomOverride: true,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  // 2. Explicit environment variable override via Vite
  const rawEnvAuthDomain = (import.meta as any).env?.VITE_FIREBASE_AUTH_DOMAIN || (import.meta as any).env?.VITE_AUTH_DOMAIN;
  if (rawEnvAuthDomain) {
    const envAuthDomain = cleanDomain(rawEnvAuthDomain);
    return {
      environmentType: 'custom',
      hostname,
      host,
      authDomain: envAuthDomain,
      redirectUri: `${protocol}//${envAuthDomain}/__/auth/handler`,
      isCustomOverride: true,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  // 3. Cloud Run Production (rfxpress.ai.studio)
  if (hostname === 'rfxpress.ai.studio' || hostname.endsWith('.ai.studio')) {
    return {
      environmentType: 'production',
      hostname,
      host,
      authDomain: host,
      redirectUri: `${protocol}//${host}/__/auth/handler`,
      isCustomOverride: false,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  // 4. GitHub Codespaces (*.app.github.dev, *.githubpreview.dev)
  if (hostname.endsWith('.app.github.dev') || hostname.endsWith('.githubpreview.dev')) {
    return {
      environmentType: 'codespaces',
      hostname,
      host,
      authDomain: host,
      redirectUri: `${protocol}//${host}/__/auth/handler`,
      isCustomOverride: false,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  // 5. AI Studio Dev / Preview environment (*.run.app)
  if (hostname.includes('.run.app')) {
    return {
      environmentType: 'aistudio',
      hostname,
      host,
      authDomain: host,
      redirectUri: `${protocol}//${host}/__/auth/handler`,
      isCustomOverride: false,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  // 6. Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return {
      environmentType: 'localhost',
      hostname,
      host,
      authDomain: defaultDomain,
      redirectUri: defaultRedirectUri,
      isCustomOverride: false,
      upstreamDefaultDomain: defaultDomain,
      upstreamDefaultRedirectUri: defaultRedirectUri,
    };
  }

  // 7. Fallback for custom servers
  return {
    environmentType: 'custom',
    hostname,
    host,
    authDomain: host,
    redirectUri: `${protocol}//${host}/__/auth/handler`,
    isCustomOverride: false,
    upstreamDefaultDomain: defaultDomain,
    upstreamDefaultRedirectUri: defaultRedirectUri,
  };
}

export function setCustomAuthDomain(domain: string | null) {
  if (typeof window === 'undefined') return;
  if (!domain) {
    window.localStorage.removeItem('rfx_custom_auth_domain');
  } else {
    window.localStorage.setItem('rfx_custom_auth_domain', domain);
  }
}

// Compute dynamic context-aware Firebase configuration
export const currentAuthInfo = detectEnvironmentAuth();
const dynamicFirebaseConfig = {
  ...firebaseConfig,
  authDomain: currentAuthInfo.authDomain,
};

// Initialize Firebase SDK
const app = initializeApp(dynamicFirebaseConfig);
export const db = getFirestore(app, (firebaseConfig as any).databaseId || (firebaseConfig as any).firestoreDatabaseId);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const storage = getStorage(app);

// Error Handling
export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId: string | undefined;
    email: string | null | undefined;
    emailVerified: boolean | undefined;
    isAnonymous: boolean | undefined;
    tenantId: string | null | undefined;
    providerInfo: {
      providerId: string;
      displayName: string | null;
      email: string | null;
      photoUrl: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData.map(provider => ({
        providerId: provider.providerId,
        displayName: provider.displayName,
        email: provider.email,
        photoUrl: provider.photoURL
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Test Connection
async function testConnection() {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Please check your Firebase configuration.");
    }
  }
}
testConnection();

export { 
  signInWithPopup, 
  onAuthStateChanged, 
  collection, 
  onSnapshot, 
  query, 
  where, 
  setDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc,
  getDoc,
  getDocs,
  Timestamp,
  serverTimestamp,
  ref,
  uploadBytes,
  getDownloadURL
};
export type { User };
